import {
  BLOCKED_TYPE_TEXT,
  BlockedCourseChipGroup,
  BlockedExamPasserFields,
  calculateBlockedDuration,
} from '@exam-center/blocked-user-management';
import { BlockedRecordBlockedScope } from '@exam-center/graphql';
import { Flex, Text } from '@shared/components';
import { dayjs, formatDate } from '@shared/utils';
import { useMemo } from 'react';

import { useBlockedRecordDetails } from '../../providers';

export default function BlockedExamPasserReadonlyDetails() {
  const { blockedRecord } = useBlockedRecordDetails();

  const blockedAt = blockedRecord.blockedAt;
  const blockedUntil = blockedRecord.blockedUntil;
  const blockedCourses = blockedRecord.blockedRecordCourses.map((course) => ({
    code: course.examCourse?.code ?? '',
    name: course.examCourse?.name ?? '',
  }));
  const { examEvent } = blockedRecord;

  const isBlockedAllScope = blockedRecord.blockedScope === BlockedRecordBlockedScope.AllScope;

  const examEventSessionInfo = useMemo(() => {
    if (!examEvent?.sessions.length) return null;
    const examDate = formatDate(examEvent.sessions.at(0)?.startedAt);
    const examStartedAt = dayjs(examEvent.sessions.at(0)?.startedAt);
    const examEndedAt = dayjs(examEvent.sessions.at(-1)?.endedAt);
    return {
      examDate,
      startedAt: examStartedAt.format('HH:mm'),
      endedAt: examEndedAt.format('HH:mm'),
      round: examEvent.sessions.at(0)?.round.toString(),
    };
  }, [examEvent?.sessions]);

  const totalBlockedDurationText = useMemo(() => {
    if (!blockedAt || !blockedUntil) return '-';

    return calculateBlockedDuration(blockedAt, blockedUntil);
  }, [blockedAt, blockedUntil]);

  const examCenterName = useMemo(() => {
    return blockedRecord.examCenter?.code && blockedRecord.examCenter.nameTH
      ? `${blockedRecord.examCenter?.code}: ${blockedRecord.examCenter.nameTH}`
      : '-';
  }, [blockedRecord.examCenter]);

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(6),
      })}
    >
      <BlockedExamPasserFields
        fields={{
          blockedType: {
            mode: 'view',
            value: BLOCKED_TYPE_TEXT[blockedRecord?.blockedType],
          },
          blockedCourseType: {
            mode: 'view',
            value: isBlockedAllScope ? (
              <Text>ระงับสิทธิ์ทุกหลักสูตร</Text>
            ) : (
              <BlockedCourseChipGroup examCourseInfos={blockedCourses} />
            ),
            slotProps: {
              icon: { sx: { display: 'none' } },
            },
          },
          startedAt: {
            mode: 'view',
            value: formatDate(blockedAt),
          },
          endedAt: {
            mode: 'view',
            value: blockedUntil ? formatDate(blockedUntil) : '-',
          },
          remark: {
            mode: 'view',
            value: blockedRecord?.blockedReason || '-',
          },
          examDate: {
            mode: 'view',
            value: examEventSessionInfo?.examDate ?? '-',
          },
          examRound: {
            mode: 'view',
            value: examEventSessionInfo?.round ?? '-',
          },
          examStartTime: {
            mode: 'view',
            value: examEventSessionInfo?.startedAt ?? '-',
          },
          examEndTime: {
            mode: 'view',
            value: examEventSessionInfo?.endedAt ?? '-',
          },
          examCenter: {
            mode: 'view',
            value: examCenterName,
          },
        }}
        options={{
          shouldDisplayEndedAtAsDate: true,
          shouldDisplayBlockedCourseSelection: false,
          shouldDisplayExamCenterSelection: true,
        }}
        slots={{
          endedAtDateEndAdornment: (
            <Text sx={(theme) => ({ pb: theme.spacing(3) })}>
              (รวมระยะเวลาระงับสิทธิ์ทั้งหมด: {totalBlockedDurationText})
            </Text>
          ),
          blockedAtLabelText: 'วันที่เริ่มระงับสิทธิ์',
          blockedUntilLabelText: 'วันที่สิ้นสุดระงับสิทธิ์',
        }}
      />
    </Flex>
  );
}
